export abstract class FormValidationService {
    /**
     * Validates that provided input string is not empty.
     * @param {string} input Input value.
     * @returns {boolean} Returns true if provided input is not empty string. Otherwise returns false.
     */
    public static validateNotEmpty(input: string): boolean {
        return input !== "";
    }

    /**
     * Validates that provided input string's length is not less than specified minLength.
     * @param {string} input Input value. 
     * @param {number} minLength Minimum valid length for the provided input value.
     * @returns {boolean} Returns true if provided input value's length is not less than minLength.
     */
    public static validateMinLength(input: string, minLength: number): boolean {
        return input.length >= minLength;
    }

    /**
     * Validates that provided input string's length is not bigger than specified maxLength.
     * @param {string} input Input value.
     * @param {number} maxLength Maximum valid length for the provided input value.
     * @returns {boolean} Returns true if provided input value's length is not bigger than maxLength.
     */
    public static validateMaxLength(input: string, maxLength: number): boolean {
        return input.length <= maxLength;
    }

    /**
     * Validates that email input contains single '@' symbol.
     * @param {string} emailAddress Email input.
     * @returns {Array<string>} Returns array with error messages. If input is valid empty array is returned.
     */
    private static validateContainsSingleAtSymbol(emailAddress: string): Array<string> {
        const validationResult: Array<string> = [];
        
        if(!emailAddress.includes('@')) {
            validationResult.push('Email address must contain single \'@\' symbol.');
        }

        if (emailAddress.split('@').length > 2) {
            validationResult.push('Email address can not contain more than one \'@\' symbol.');
        }

        return validationResult;
    }

    public static validateEmail(emailAddress: string): Array<string> {
        const validationResult: Array<string> = [];
        const isEmailProvided: boolean = this.validateNotEmpty(emailAddress);
        
        if (!isEmailProvided) {
            validationResult.push("Email field is required.");
            return validationResult;
        }
        
        const atSymbolErrorMessages: Array<string> = this.validateContainsSingleAtSymbol(emailAddress);
        if (atSymbolErrorMessages.length === 0) {
            const emailParts: Array<string> = emailAddress.split('@');
            
            //#region Local part validations
            const localPart: string = emailParts[0];
            const localPartMinLength: number = 3;
            const localPartMaxLength: number = 50;
            const localPartErrorMessages: Array<string> = [];

            const isLocalPartMinLengthCompatible: boolean = this.validateMinLength(localPart, localPartMinLength);
            if (!isLocalPartMinLengthCompatible) {
                localPartErrorMessages.push(`Local part length can not be less than ${localPartMinLength} symbols.`);
            }

            const isLocalPartMaxLengthCompatible: boolean = this.validateMaxLength(localPart, localPartMaxLength);
            if (!isLocalPartMaxLengthCompatible) {
                localPartErrorMessages.push(`Local part length can not exceed ${localPartMaxLength} symbols.`);
            }

            const isStartingWithDot: boolean = localPart[0] === ".";
            if (isStartingWithDot) {
                localPartErrorMessages.push(`Local part can not start with '.' symbol.`);
            }

            const isEndingWithDot: boolean = localPart[localPart.length - 1] === ".";
            if(isEndingWithDot) {
                localPartErrorMessages.push(`Local part can not end with '.' symbol.`);
            }

            localPartErrorMessages.forEach(errorMessage => {
                validationResult.push(errorMessage);
            });
            //#endregion

            //#region Domain part validations
            const domainPart: string = emailParts[1];
            const domainPartMinLength: number = 4;
            const domainPartMaxLength: number = 50;
            const domainPartErrorMessages: Array<string> = [];

            const isDomainPartMinLengthCompatible: boolean = this.validateMinLength(domainPart, domainPartMinLength);
            if (!isDomainPartMinLengthCompatible) {
                domainPartErrorMessages.push(`Domain part length can not be less than ${domainPartMinLength} symbols.`);
            }

            const isDomainPartMaxLengthCompatible: boolean = this.validateMaxLength(domainPart, domainPartMaxLength);
            if (!isDomainPartMaxLengthCompatible) {
                domainPartErrorMessages.push(`Domain part length can not exceed ${domainPartMaxLength} symbols.`);
            }

            // Validate domain is not only top level domain.
            const containsAtLeast2DNSLabels: boolean = domainPart.includes('.');
            if (domainPart !== "" && isDomainPartMinLengthCompatible && !containsAtLeast2DNSLabels) {
                domainPartErrorMessages.push(`Invalid domain. Email addresses with only top level domain are not allowed.`);
            }

            const DNSLabels: Array<string> = domainPart.split('.');

            // Validate count of DNS labels.
            const containsMoreThanFourDNSLabels: boolean = DNSLabels.length > 4;
            if (containsMoreThanFourDNSLabels) {
                domainPartErrorMessages.push(`Domain part can not contain more than 4 DNS labels.`);
            }
            
            // Validate that top level domain contains symbol which is different than number.
            let isTopLevelDomainContainingNonNumberSymbol: boolean = false;
            const lastDNSLabel = DNSLabels[DNSLabels.length - 1];
            if(lastDNSLabel !== "") {
                for (let index = 0; index < lastDNSLabel.length; index++) {
                    const character = lastDNSLabel[index];
                    const charASCIIvalue = character.charCodeAt(0);
    
                    // Numbers in ASCII table are between 48 and 57 included.
                    if (charASCIIvalue < 48 || charASCIIvalue > 57) {
                        isTopLevelDomainContainingNonNumberSymbol = true;
                        break;
                    }
                }
    
                if (isDomainPartMinLengthCompatible && !isTopLevelDomainContainingNonNumberSymbol) {
                    domainPartErrorMessages.push(`Top level domain can not be all numeric.`);
                }
            }

            // Validate DNS labels length.
            const areDNSLabelsLengthValid: boolean = domainPart.split('.').every(dnsLabel => dnsLabel.length <= 64);
            if (!areDNSLabelsLengthValid) {
                domainPartErrorMessages.push(`DNS labels length can not exceed 64 symbols.`);
            }

            const isDomainStartingWithDash: boolean = domainPart[0] === "-";
            if (isDomainStartingWithDash) {
                domainPartErrorMessages.push(`Domain part can not start with '-' symbol.`);
            }

            const isDomainEndingWithDash: boolean = domainPart[domainPart.length - 1] === "-";
            if(isDomainEndingWithDash && isDomainPartMinLengthCompatible) {
                domainPartErrorMessages.push(`Domain part can not end with '-' symbol.`);
            }

            domainPartErrorMessages.forEach(errorMessage => {
                validationResult.push(errorMessage);
            });
            //#endregion
        } else {
            atSymbolErrorMessages.forEach(errorMessage => {
                validationResult.push(errorMessage);
            });
        }

        return validationResult;
    }

    /**
     * Validates if it's provided link to GitHub profile or not.
     * @param {string} input Input's value.
     * @returns {boolean} Returns true if provided value is URL to GitHub.
     */
    public static validateGitHubProfileInput(input: string): boolean {
        return input.startsWith("https://github.com/") || input.startsWith("https://www.github.com/");
    }

    /**
     * Validates if it's provided link to LinkedIn profile or not.
     * @param {string} input Input's value.
     * @returns {boolean} Returns true if provided value is URL to LinkedIn.
     */
    public static validateLinkedInProfileInput(input: string): boolean {
        return input.startsWith("https://linkedin.com") || input.startsWith("https://www.linkedin.com");
    }

    /**
     * Clears error messages DIV container's content.
     * @param messagesContainerSelector CSS selector for the DIV container which content should be emptied.
     * @returns {void} Void.
     */
    public static clearErrorMessages(messagesContainerSelector: string): void {
        const container: HTMLDivElement | null = document.querySelector(messagesContainerSelector);
        if (container === null) {
            console.error(`Invalid selector provided: ${messagesContainerSelector}. Can not clear error messages.`);
        } else {
            container.innerHTML = "";
        }
    }

    /**
     * Appends error messages inside DIV container.
     * @param {string} messagesContainerSelector CSS selector for the DIV container which content should be populated with error messages.
     * @param {Array<string>} errorMessages Collection of error messages that should be added inside DIV container.
     * @returns {void} Void. 
    */
    public static addErrorMessages(messagesContainerSelector: string, errorMessages: Array<string>): void {
        const container: HTMLDivElement | null = document.querySelector(messagesContainerSelector);
        if (container === null) {
            console.error(`Invalid selector provided: ${messagesContainerSelector}. Can not append error messages.`);
        } else {
            this.clearErrorMessages(messagesContainerSelector);

            errorMessages.forEach(errorMessage => {
                const spanElement = document.createElement('div');
                spanElement.textContent = errorMessage;
                spanElement.classList.add('error-message'); // red color. 

                container.appendChild(spanElement);
            });
        }
    }
}